import { Component } from '@theme/component';

/**
 * Lightweight controller for simple resource-list carousels.
 *
 * Native scrolling + CSS Scroll Snap handle the actual movement. This component
 * only handles arrow clicks and whether the edge arrows should be disabled.
 * It intentionally has no pointermove/touchmove/mousemove/scroll listeners.
 */
class ResourceListCarousel extends Component {
  requiredRefs = ['scroller'];

  /** @type {IntersectionObserver | null} */
  #edgeObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #scrollDistance = 0;

  /** @type {1 | -1} */
  #directionMultiplier = 1;

  /** @type {boolean} */
  #scrollScheduled = false;

  /** @type {MediaQueryList} */
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    this.#edgeObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    super.disconnectedCallback();
  }

  /**
   * Move one viewport toward the previous group of cards.
   * @param {Event} event
   */
  previous(event) {
    event?.preventDefault();
    this.#queueScroll(-1);
  }

  /**
   * Move one viewport toward the next group of cards.
   * @param {Event} event
   */
  next(event) {
    event?.preventDefault();
    this.#queueScroll(1);
  }

  #setup() {
    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    // Read direction outside the interaction path.
    this.#directionMultiplier = getComputedStyle(scroller).direction === 'rtl' ? -1 : 1;

    // Cache the page distance outside click handlers. ResizeObserver only runs
    // when the carousel size actually changes, not while the user scrolls it.
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => {
      this.#scrollDistance = scroller.clientWidth;
    });
    this.#resizeObserver.observe(scroller);
    this.#scrollDistance = scroller.clientWidth;

    this.#setupEdgeObserver(scroller);
  }

  /**
   * Keep the click callback tiny. The native scroll is scheduled for the next
   * animation frame and uses only cached geometry.
   * @param {1 | -1} direction
   */
  #queueScroll(direction) {
    if (this.#scrollScheduled) return;

    const { scroller } = this.refs;
    if (!(scroller instanceof HTMLElement)) return;

    this.#scrollScheduled = true;

    requestAnimationFrame(() => {
      this.#scrollScheduled = false;

      const distance = this.#scrollDistance || scroller.clientWidth;

      scroller.scrollBy({
        left: distance * direction * this.#directionMultiplier,
        behavior: this.#reducedMotion.matches ? 'auto' : 'smooth',
      });
    });
  }

  /**
   * Observe only the first and last cards so arrows can disappear at the edges.
   * No scroll handler or per-frame geometry calculations are needed.
   * @param {HTMLElement} scroller
   */
  #setupEdgeObserver(scroller) {
    this.#edgeObserver?.disconnect();

    const slides = Array.from(scroller.children).filter(
      (element) => element instanceof HTMLElement && element.hasAttribute('data-resource-list-carousel-slide')
    );

    const previous = this.refs.previous;
    const next = this.refs.next;

    if (previous instanceof HTMLButtonElement) previous.disabled = true;
    if (next instanceof HTMLButtonElement) next.disabled = slides.length <= 1;

    if (slides.length <= 1) return;

    const firstSlide = slides[0];
    const lastSlide = slides[slides.length - 1];

    this.#edgeObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const fullyAtEdge = entry.isIntersecting && entry.intersectionRatio >= 0.98;

          if (entry.target === firstSlide && previous instanceof HTMLButtonElement) {
            previous.disabled = fullyAtEdge;
          }

          if (entry.target === lastSlide && next instanceof HTMLButtonElement) {
            next.disabled = fullyAtEdge;
          }
        }
      },
      {
        root: scroller,
        threshold: 0.98,
        rootMargin: '0px 1px',
      }
    );

    this.#edgeObserver.observe(firstSlide);
    this.#edgeObserver.observe(lastSlide);
  }
}

if (!customElements.get('resource-list-carousel-component')) {
  customElements.define('resource-list-carousel-component', ResourceListCarousel);
}
